// Fetcher + React Query hook for the Index Performance Analytics dashboard.
//
// Auth: we reuse the project's shared authenticated Axios instance
// (`apiClient` from src/api/client.ts). Its baseURL already includes the
// `/api/v1` version prefix and attaches the WordPress-issued JWT
// (`Authorization: Bearer <token>`) + the `forge_session` cookie on every
// request, and centralises 401 -> silent refresh -> WP-login-bounce
// handling. Because `/api/v1` is baked into baseURL, BASE_URL below is the
// BARE domain path only (mirrors the treasury-curve sibling).

import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../../../../api/client';
import type {
  AssetChartResponse,
  PerformanceDashboardResponse,
} from '../types';

// NOTE: apiClient.baseURL already contains `/api/v1`, so we must NOT repeat
// it here or every request would 404 on `/api/v1/api/v1/...`. The backend
// mounts this feature at:
//   /api/v1/market-analysis/index-performance/latest
const BASE_URL = '/market-analysis/index-performance';

// --- FETCHER ------------------------------------------------------------
export const fetchLatestPerformance =
  async (): Promise<PerformanceDashboardResponse> => {
    const { data } = await apiClient.get<PerformanceDashboardResponse>(
      `${BASE_URL}/latest`,
    );
    return data;
  };

// --- HOOK ---------------------------------------------------------------
export const useLatestPerformance = () => {
  return useQuery({
    queryKey: ['index-performance', 'latest'],
    queryFn: fetchLatestPerformance,
    // The background ARQ worker refreshes this snapshot once daily (22:00
    // UTC), so we cache heavily to avoid needless refetches.
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
};

// --- CHART FETCHER ------------------------------------------------------
// Proxies ~3 years (800 trading days) of close/volume history from FMP via
// the backend GET /{symbol}/chart route. FMP index tickers carry a leading
// caret (e.g. `^GSPC`), so the symbol MUST be URL-encoded (`%5EGSPC`) or the
// path segment breaks.
export const fetchAssetChart =
  async (symbol: string): Promise<AssetChartResponse> => {
    const { data } = await apiClient.get<AssetChartResponse>(
      `${BASE_URL}/${encodeURIComponent(symbol)}/chart`,
    );
    return data;
  };

// --- CHART HOOK ---------------------------------------------------------
// `enabled` lets the modal defer the fetch until a symbol is actually
// selected (pass the modal-open flag / a truthy symbol). The underlying FMP
// history only advances once per trading day, so we cache for an hour.
export const useAssetChart = (symbol: string | null, enabled = true) => {
  return useQuery({
    queryKey: ['index-performance', 'chart', symbol],
    queryFn: () => fetchAssetChart(symbol as string),
    enabled: enabled && Boolean(symbol),
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};
